// P4-CC20 — date safety primitives.
//
// THE DEFECT THIS FILE EXISTS TO STOP: `new Date(y, m, d).toISOString().slice(0,10)`
// (or `new Date().toISOString().slice(0,10)`, or `new Date("YYYY-MM-DD")`) all round-trip
// a calendar date through UTC. For ANY positive UTC offset — Ireland is UTC+1 for roughly
// seven months a year (BST) — local midnight is 23:00 the PREVIOUS day in UTC, so
// `.toISOString().slice(0,10)` silently returns yesterday's date. Found in production in
// CC18 (a VAT return's default quarter saved as 2026-06-30 -> 2026-09-29 instead of
// 2026-07-01 -> 2026-09-30); traced across 37 more call sites in CC20 Phase 0, including
// invoices.js's `dueDateFrom()`, which is wrong on EVERY call during BST, not just near
// midnight, because it constructs its Date object AT local midnight before converting.
//
// THE RULE: never call `.toISOString()` on a value you intend to read back as a calendar
// date, and never construct a Date from a bare "YYYY-MM-DD" string (`new Date(iso)`
// parses date-only strings as UTC midnight — the OPPOSITE convention from the numeric
// `new Date(y, m, d)` constructor, which is local). Every function below exists to be the
// one place that gets this right, so nobody has to re-derive it at a call site again.
// If you are about to "simplify" one of these back to `.toISOString()` — don't. That is
// the exact bug this file was written to delete from the codebase.

/**
 * "YYYY-MM-DD" from a Date's LOCAL calendar components. Never touches
 * `.toISOString()` — that's the whole point. Use this everywhere a Date object needs to
 * become the string you'd read off a calendar / a native `<input type="date">`.
 * @param {Date} date
 * @returns {string}
 */
export function localISO(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/**
 * A Date at LOCAL midnight for a "YYYY-MM-DD" string. Never `new Date(iso)` — for a
 * date-only string, that parses as UTC midnight, which is a different instant from local
 * midnight for any non-zero UTC offset. Uses the numeric `new Date(y, m, d)` constructor,
 * which the spec defines as local, to land on the instant you actually mean.
 * @param {string} iso "YYYY-MM-DD"
 * @returns {Date}
 */
export function parseLocalDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Today's date, as the local calendar would show it, "YYYY-MM-DD". The replacement for
 * every `today()`/`todayISO()` copy in the codebase — those are correct 23 hours out of
 * 24 and wrong for the hour between local midnight and 01:00 during BST, because they
 * capture the real current instant and then round-trip it through `.toISOString()`.
 * @returns {string}
 */
export function todayISO() {
  return localISO(new Date());
}

/**
 * "YYYY-MM-DD" plus n calendar days (n may be negative). Deliberately calendar
 * arithmetic (parse to local midnight, `setDate`, format back) rather than millisecond
 * arithmetic (`Date.now() + n * 86400000`) — millisecond arithmetic drifts by an hour
 * across any DST transition inside the window, which can land a "day-only" figure on the
 * wrong calendar day right at the boundary. `setDate`/`getDate` operate on local
 * components and are DST-safe by construction.
 * @param {string} iso "YYYY-MM-DD" @param {number} n days to add (negative to subtract)
 * @returns {string}
 */
export function addDaysISO(iso, n) {
  const d = parseLocalDate(iso);
  d.setDate(d.getDate() + n);
  return localISO(d);
}

/**
 * "YYYY-MM-DD" for the 1st of the current LOCAL month. Moved here from
 * StatementManager.jsx, which had it right already (built from local components, no
 * `.toISOString()` in sight) — centralised so this file is the one place date logic
 * lives, not because it was ever wrong.
 * @returns {string}
 */
export function startOfMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * Whole calendar days between two "YYYY-MM-DD" dates (b - a; negative if b is before a).
 * Modelled on customerIntelligence.js's `daysBetween` — parse BOTH sides to local
 * midnight and difference those, never mix an absolute instant (`Date.now()`/`nowMs`)
 * against a midnight value the way a naive "days overdue" calculation tends to. Rounds
 * rather than floors: a local-midnight-to-local-midnight span that crosses a DST
 * transition is off by exactly one hour in milliseconds (the clock gains or loses an
 * hour), which without rounding would floor a genuine N-day span down to N-1 on the
 * "spring forward" (GMT -> BST) side.
 * @param {string} aIso "YYYY-MM-DD" @param {string} bIso "YYYY-MM-DD"
 * @returns {number}
 */
export function daysBetweenISO(aIso, bIso) {
  return Math.round((parseLocalDate(bIso).getTime() - parseLocalDate(aIso).getTime()) / 86400000);
}
